import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { documentApi } from '../services/api';
import type { DocumentRecord, MessageAttachment } from '../types';
import { DocumentPreviewModal, useDocumentPreview } from './DocumentPreviewModal';

/** An image attachment as a small picture, loaded with the user's session. */
function AttachmentThumbnail({ attachment }: { attachment: MessageAttachment }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    documentApi.getFileBlob(attachment.id).then(blob => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
    }).catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id]);
  return url
    ? <img src={url} alt="" className="message-attachment-thumb" />
    : <span className="message-attachment-thumb is-loading" aria-hidden="true"><i className="feather-image"></i></span>;
}

/**
 * The files sent with a message (ClickUp 869fj6n1d): pictures as thumbnails,
 * other files by name. A click opens the file in the document preview.
 */
export function MessageAttachments({ attachments }: { attachments?: MessageAttachment[] }) {
  const { t } = useTranslation();
  const preview = useDocumentPreview();
  if (!attachments || attachments.length === 0) return null;
  const open = (attachment: MessageAttachment) =>
    void preview.openPreview({ id: attachment.id, originalName: attachment.originalName, mimeType: attachment.mimeType } as DocumentRecord);

  return (
    <>
      <ul className="message-attachments" aria-label={t('messageAttachments.sent')}>
        {attachments.map(attachment => {
          const image = attachment.mimeType.startsWith('image/');
          return (
            <li key={attachment.id}>
              <button type="button" className={`message-attachment${image ? ' is-image' : ''}`} onClick={() => open(attachment)}
                aria-label={t('messageAttachments.open', { name: attachment.originalName })} data-testid="message-attachment">
                {image ? <AttachmentThumbnail attachment={attachment} /> : <i className="feather-file-text" aria-hidden="true"></i>}
                {!image && <span className="message-attachment-name">{attachment.originalName}</span>}
              </button>
            </li>
          );
        })}
      </ul>
      <DocumentPreviewModal
        previewDoc={preview.previewDoc}
        previewUrl={preview.previewUrl}
        previewHtml={preview.previewHtml}
        previewLoading={preview.previewLoading}
        previewError={preview.previewError}
        previewErrorText={preview.previewErrorText}
        onClose={preview.closePreview}
      />
    </>
  );
}
